import { Text } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { Redirect } from 'react-router-dom';

import { LEAccordion, LEAccordionItem } from '../../components/LEAccordion';
import LEAccordionHeader from '../../components/LEAccordion/LEAccordionHeader';
import LEAccordionContainer from '../../components/LEAccordion/LeAccordionContainer';
import { ACCORDION_NAME } from '../../components/LEAccordion/utils';
import TextWithTooltip from '../../components/TextWithTooltip';
import BackButton from '../../components/ui/buttons/BackButton';
import { ButtonWrapper } from '../../components/ui/buttons/ButtonWrapper';
import { getTempInfoCountryConfig } from '../../constants/countries';
import { LDFlags } from '../../constants/flags';
import { LOAN_APPLICATION } from '../../constants/urls';
import { LienholderRequirement } from '../../gql/generated/graphql';
import { useFlag, useTempInfo } from '../../hooks';
import { usePayoffIntelligence } from '../../hooks/useAutoFetchPayoff/usePayoffIntelligence';
import { sellsViaMarketplace } from '../../utils/tempInfo';
import PayoffAmountAndInstructions from './PayoffAmountAndInstructions';
import PayoffIntelligenceMissingFieldsAccordionItem from './PayoffIntelligenceMissingFieldsAccordionItem';

const HOW_IS_PAYOFF_CALCULATED =
  "Your payoff includes your car's residual value (what the car was expected to be worth at the " +
  'end of the lease, determined when your lease began), plus any remaining lease payments and ' +
  'outstanding interest. These are combined by your lender into one total payoff amount to ' +
  'purchase the car.';

const PayoffInstructions = () => {
  const keepMyCarSelectorEnabled = useFlag(LDFlags.KEEP_MY_CAR_SELECTOR);

  const { info } = useTempInfo({ fetchPolicy: 'network-only' });
  const { checkLienholderSupportedForPI } = usePayoffIntelligence();

  const [piSupportLoading, setPiSupportLoading] = useState(true);
  const [piSupport, setPiSupport] = useState<{
    isSupported: boolean;
    requiredFields: string[];
    missingRequiredFields: LienholderRequirement[];
  }>({ isSupported: false, requiredFields: [], missingRequiredFields: [] });

  useEffect(() => {
    let isMounted = true;
    const check = async () => {
      const { isSupported, requiredFields, missingRequiredFields } =
        await checkLienholderSupportedForPI();
      if (isMounted) {
        setPiSupport({ isSupported, requiredFields, missingRequiredFields });
        setPiSupportLoading(false);
      }
    };
    check();
    return () => {
      isMounted = false;
    };
  }, [checkLienholderSupportedForPI, info?.data?.lienholder_slug]);

  const tempInfoData = info?.data;
  const wantsToSell = keepMyCarSelectorEnabled && sellsViaMarketplace(tempInfoData);

  const shouldCollectMissingFieldsOnPayoffPage =
    wantsToSell && piSupport.isSupported && piSupport.missingRequiredFields.length > 0;

  if (!getTempInfoCountryConfig(tempInfoData).payoffCollection) {
    return <Redirect to={LOAN_APPLICATION} />;
  }

  return (
    <LEAccordionContainer>
      <LEAccordionHeader>Your Payoff Amount</LEAccordionHeader>
      <Text textAlign="center" fontSize="16px" mb="5px">
        Your payoff is the amount it costs to purchase your car today.
        <br />
        <TextWithTooltip label={HOW_IS_PAYOFF_CALCULATED}>
          How a payoff is calculated?
        </TextWithTooltip>
      </Text>
      <LEAccordion name={ACCORDION_NAME.PAYOFF_ACCORDION} minutes={5}>
        {piSupportLoading ? (
          <LEAccordionItem accordionKey="payoffLoading" title="LOADING" initialStatus="loading" />
        ) : shouldCollectMissingFieldsOnPayoffPage ? (
          <PayoffIntelligenceMissingFieldsAccordionItem
            missingFields={piSupport.missingRequiredFields}
          />
        ) : (
          <PayoffAmountAndInstructions />
        )}
      </LEAccordion>
      <ButtonWrapper>
        <BackButton />
      </ButtonWrapper>
    </LEAccordionContainer>
  );
};

export default PayoffInstructions;
